import * as React from "react";
import { Formik, Form, Field } from "formik";
import { Routine } from "redux-saga-routines";
import { connect } from "react-redux";
import {Action, ActionFunctionAny} from "redux-actions";
import styled from "styled-components";
import { Collapse } from "react-collapse";
import { WrappedComponentProps, injectIntl, useIntl } from "react-intl";
import { CheckWrap } from "@Elements/Form/CheckWrap";
import SettingTab from "@Safemate/Settings/Components/settingTab";
import Tooltip from "@Elements/Tooltip";
import { AppState } from "@Safemate/Store/types";
import { InactivitySettings } from "../../types";
import { saveInactivity } from "@Safemate/Settings/Store/routines";
import { SettingEnum } from "@Safemate/Settings/settingEnum";
import { DropdownWrap } from "@Elements/Form/DropdownWrap";
import { inactivityOptions, warnTimeOptions } from "@Safemate/Settings/EventConfig/options";
import { TIME_OFFSET, status } from "@Elements/Form/utils";
import { FieldWrapper, InactivityWrapper, PeriodWrapper, StyledInput  } from "@Safemate/Settings/EventConfig/styles";
import { SettingHeader, TitleWrap } from "@Elements/Form/styles";
import InformationIcon from "@Elements/Icon/Icons/InformationIcon";
import {
  SettingsLayout,
  Section,
  SectionTitle,
  SectionDesc,
  FieldGrid,
  ScheduleGrid,
  ScheduleCard,
  ScheduleCardHeader,
  FieldLabel,
  TimeRange,
  TimeField,
  TimeSeparator,
  TooltipAlign
} from "./settingsLayout";

interface InactivityProps{
  dehaId: number;
  deviId: number;
  inactivity: InactivitySettings;
  saveInactivity: Routine<ActionFunctionAny<Action<any>>>;
}

const Inactivity = ({ dehaId, deviId, inactivity, saveInactivity }: InactivityProps) => {

  console.log(inactivity);

  return (
    <SettingTab
      dehaId={dehaId}
      deviId={deviId}
      settings={inactivity}
      save={saveInactivity}
      settingEnum={SettingEnum.INACTIVITY_EVENT}
      header={{
        title: "noActivityAlarm",
        tooltip: "inactivitySettingsTabToolTipText",
        tooltipTitle: "noActivityAlarm",
        toggle: true,
        toggleValue: ["inactivityFunction"]
      }}
    />
  )
}


/*
 <SettingsHeader 
          title={formatMessage({id: "noActivityAlarm", defaultMessage: "noActivityAlarm"})}
          tooltip="inactivitySettingsTabToolTipText"
          tooltipTitle={{
            id: "noActivityAlarm",
            defaultMessage: "noActivityAlarm"
          }}
        >
          <Toggle
            checked={inactivityFunction}
            onChange={(value: boolean) => setFieldValue("inactivity.inactivityFunction", value)}
            />
        </SettingsHeader>
        */

const mapStateToProps = ({ settings: {initialization: { deviId, dehaId }, general: {inactivity}}}: AppState) => {
  return {
    dehaId,
    deviId,
    inactivity,
  }
}

const mapDispatchToProps = {
  saveInactivity,
}

export default connect(mapStateToProps, mapDispatchToProps)(Inactivity);


interface InactivityFieldProps{
  intervalName: string;
  periodToName: string;
  periodFromName: string;
  count: number;
  prefix?: string;
}

export const InactivityField = ({intervalName, periodToName, periodFromName, count, prefix }: InactivityFieldProps) => {

  const { formatMessage } = useIntl();

  const fieldName = (name: string) => prefix ? `${prefix}.${name}` : name;

  const Interval = DropdownWrap(
    intervalName, 
    {
      prefix,
      title: {id: "interval", defaultMessage: "Intervall"},
      tooltip: "inactivityLimitToolTipText",
      tooltipTitle: {id: "inactivityLimitToolTipTitle", defaultMessage: "Inaktivitetsgrense"},
      dataOptions: {
        options: inactivityOptions,
        offset: TIME_OFFSET.SECONDS_IN_SECOND
      },
      width: "100%"
    }
  );

  return (
    <ScheduleCard>
      <ScheduleCardHeader>
        {`${formatMessage({id: "inactivitySchedule", defaultMessage: "Tidsskjema"})} ${count}`}
      </ScheduleCardHeader>
      <Interval/>
      <div>
        <FieldLabel>
          {formatMessage({id: "inactivityActiveWindowLabel", defaultMessage: "Aktivt tidsrom"})}
        </FieldLabel>
        <TimeRange>
          <TimeField>
            <FieldLabel>
              {formatMessage({id: "inactivityFromParagraphTitle", defaultMessage: "Fra"})}
            </FieldLabel>
            <StyledInput
              type="time"
              name={fieldName(periodFromName)}
              step="60"
            />
          </TimeField>
          <TimeSeparator>–</TimeSeparator>
          <TimeField>
            <FieldLabel>
              {formatMessage({id: "inactivityToParagraphTitle", defaultMessage: "Til"})}
            </FieldLabel>
            <StyledInput
              type="time"
              name={fieldName(periodToName)}
              step="60"
            />
          </TimeField>
          <TooltipAlign>
            <Tooltip
              labelId="inactivityPeriodToolTipText"
              defaultMessage="Her angis tidsrommet det aksepteres at man ikke måler livstegn."
              popoverId={`${periodFromName}-popover`}
              title={formatMessage({id: "inactivityPeriodToolTipTitle", defaultMessage: "Periode"})}
            >
              <InformationIcon size="medium" />
            </Tooltip>
          </TooltipAlign>
        </TimeRange>
      </div>
    </ScheduleCard>
  );
};

export const InactivityWarnTime = 
  DropdownWrap(
    "inactivityWarnTime", 
    {
      title: {id: "inactivityAlertToolTipTitle", defaultMessage: "Inaktivitetsgrense"},
      tooltip: "inactivityAlertToolTipText",
      tooltipTitle: {id: "inactivityAlertToolTipTitle", defaultMessage: "Inaktivitetsgrense"},
      dataOptions: {
        options: warnTimeOptions,
        offset: TIME_OFFSET.SECONDS_IN_SECOND,
        nullValue: status.disable
      },
      width: "100%"
    }
  );

export const GX8InactivitySection = () => {
  const { formatMessage } = useIntl();
  return(
    <SettingsLayout>
      <Section>
        <SectionTitle>
          {formatMessage({id: "inactivitySchedulesTitle", defaultMessage: "Tidsskjema"})}
        </SectionTitle>
        <SectionDesc>
          {formatMessage({
            id: "inactivitySchedulesDesc",
            defaultMessage: "Angi tidsrommene der det aksepteres at det ikke måles livstegn. Utenfor disse periodene kan inaktivitetsalarm utløses."
          })}
        </SectionDesc>
        <ScheduleGrid>
          <InactivityField 
            intervalName="inactivityIntervalOne" 
            periodToName="inactivityToOne" 
            periodFromName="inactivityFromOne"
            count={1}
          />
          <InactivityField 
            intervalName="inactivityIntervalTwo" 
            periodToName="inactivityToTwo" 
            periodFromName="inactivityFromTwo"
            count={2}
          />
        </ScheduleGrid>
      </Section>
      <Section>
        <SectionTitle>
          {formatMessage({id: "inactivityAlertToolTipTitle", defaultMessage: "Inaktivitetsgrense"})}
        </SectionTitle>
        <FieldGrid>
          <InactivityWarnTime/>
        </FieldGrid>
      </Section>
    </SettingsLayout>
  )
}